import { CheckCircle2, AlertTriangle, Info, Lightbulb, ArrowRight } from "lucide-react"

import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"
import type { HawkReview } from "@/types/phrase"

interface HawkReviewCardProps {
  review: HawkReview
  className?: string
}

/**
 * Hawk's practical peer review - appears BEFORE Guru explanation.
 * Design: Technical/diagnostic aesthetic with sharp lines and icon-driven sections.
 * Purpose: Quick scannable insights, warnings, and reality-checks.
 */
export function HawkReviewCard({ review, className }: HawkReviewCardProps) {
  const { summary, accuracyCheck, formalityCheck, pitfalls, alternatives } = review

  const hasContent = summary || accuracyCheck || formalityCheck || pitfalls?.length || alternatives

  if (!hasContent) return null

  return (
    <Card className={cn(
      "relative overflow-hidden border-slate-300/60 bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-50",
      "dark:border-slate-700/60 dark:from-slate-900 dark:via-blue-950/20 dark:to-slate-900",
      className
    )}>
      {/* Subtle technical grid pattern overlay */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.02] dark:opacity-[0.05]"
        style={{
          backgroundImage: `
            linear-gradient(to right, currentColor 1px, transparent 1px),
            linear-gradient(to bottom, currentColor 1px, transparent 1px)
          `,
          backgroundSize: "20px 20px"
        }}
      />

      {/* Header - diagnostic style */}
      <div className="relative border-b border-slate-300/60 bg-slate-100/80 px-5 py-3.5 dark:border-slate-700/60 dark:bg-slate-800/50">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="flex size-9 items-center justify-center rounded-lg bg-blue-600 shadow-sm dark:bg-blue-500">
              <Lightbulb className="size-5 text-white" strokeWidth={2.5} />
            </div>
            <div>
              <h2 className="text-sm font-bold tracking-tight text-slate-900 dark:text-slate-50">
                Reality Check
              </h2>
              <p className="text-[11px] font-medium text-slate-500 dark:text-slate-400">
                Practical peer review by Hawk
              </p>
            </div>
          </div>
          <p className="shrink-0 text-[10px] font-medium text-slate-400 dark:text-slate-500">
            Practical analysis
          </p>
        </div>
      </div>

      <CardContent className="relative space-y-4 p-5 sm:p-6">
        {/* TL;DR Summary - always first, most prominent */}
        {summary && (
          <div className="rounded-lg border-l-4 border-blue-500 bg-blue-50/50 p-4 dark:border-blue-400 dark:bg-blue-950/30">
            <div className="flex items-start gap-3">
              <Info className="mt-0.5 size-5 shrink-0 text-blue-600 dark:text-blue-400" strokeWidth={2} />
              <div>
                <p className="text-sm font-semibold text-blue-900 dark:text-blue-100">
                  Quick Take
                </p>
                <p className="mt-1 text-[15px] leading-relaxed text-slate-700 dark:text-slate-300">
                  {summary}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Accuracy Check - validation badge style */}
        {accuracyCheck && (
          <div className="flex items-start gap-3 rounded-lg border border-emerald-200/60 bg-emerald-50/40 p-3.5 dark:border-emerald-800/40 dark:bg-emerald-950/20">
            <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-emerald-600 dark:text-emerald-400" strokeWidth={2} />
            <div className="min-w-0 flex-1">
              <p className="mb-1 text-[11px] font-bold uppercase tracking-widest text-emerald-700/70 dark:text-emerald-400/70">
                Accuracy
              </p>
              <p className="text-sm leading-relaxed text-slate-700 dark:text-slate-300">
                {accuracyCheck}
              </p>
            </div>
          </div>
        )}

        {/* Formality Check - editorial note style */}
        {formalityCheck && (
          <div className="flex items-start gap-3 rounded-lg border border-violet-200/60 bg-violet-50/40 p-3.5 dark:border-violet-800/40 dark:bg-violet-950/20">
            <Badge className="mt-0.5 shrink-0 border-violet-300 bg-violet-200 text-violet-800 dark:border-violet-700 dark:bg-violet-900 dark:text-violet-200">
              Formality
            </Badge>
            <div className="min-w-0 flex-1">
              <p className="text-sm leading-relaxed text-slate-700 dark:text-slate-300">
                {formalityCheck}
              </p>
            </div>
          </div>
        )}

        {/* Pitfalls - warning list with monospace highlights */}
        {pitfalls && pitfalls.length > 0 && (
          <div className="space-y-2.5">
            <div className="flex items-center gap-2">
              <AlertTriangle className="size-4 text-amber-600 dark:text-amber-400" strokeWidth={2.5} />
              <p className="text-[11px] font-bold uppercase tracking-widest text-amber-700/70 dark:text-amber-400/70">
                Common Pitfalls
              </p>
            </div>
            <ul className="space-y-2">
              {pitfalls.map((pitfall, idx) => (
                <li
                  key={idx}
                  className="flex items-start gap-2.5 rounded-md border border-amber-200/60 bg-amber-50/40 px-3 py-2.5 dark:border-amber-800/40 dark:bg-amber-950/20"
                >
                  <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-amber-200 text-[11px] font-bold text-amber-800 dark:bg-amber-900 dark:text-amber-200">
                    {idx + 1}
                  </span>
                  <p className="text-sm leading-relaxed text-slate-700 dark:text-slate-300">
                    {pitfall}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Alternatives - actionable suggestions with arrow indicators */}
        {alternatives && (
          <div className="rounded-lg border border-slate-200 bg-white/60 p-4 dark:border-slate-700 dark:bg-slate-800/40">
            <div className="mb-2.5 flex items-center gap-2">
              <ArrowRight className="size-4 text-slate-600 dark:text-slate-400" strokeWidth={2.5} />
              <p className="text-[11px] font-bold uppercase tracking-widest text-slate-600 dark:text-slate-400">
                Better Alternatives
              </p>
            </div>
            <p className="text-sm leading-relaxed text-slate-700 dark:text-slate-300">
              {alternatives}
            </p>
          </div>
        )}

      </CardContent>
    </Card>
  )
}
